import {
  GlyphMediaVariant,
  SubscriptionLevel,
  type Post,
} from '@tribeplatform/gql-client/types'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { type SvgIconName } from '@tribeplatform/react-ui-kit/Icon'

import { EntityImage } from '../../common/components/EntityImage.js'
import { useLogin } from '../../common/hooks/useLogin.js'
import { useFirstRenderContext } from '../../common/ssr/index.js'
import { convertIdToGlyph } from '../../MediaPicker/utils/utils.js'
import { FollowAction as TFollowAction } from '../../Views/types.js'
import { getTextIconSettings } from '../utils/utils.js'

import {
  FollowLevelMenuItems,
  followLevelIcons,
  useFollowLevelLabels,
} from './followLevel.js'
import { useFollowLevel } from './useFollowLevel.js'

interface Props {
  post: Post
  action: TFollowAction
}

/** Picker glyph id for a level; undefined for a level this client does not know. */
const levelGlyphId = (level: SubscriptionLevel): string | undefined => {
  const icon: SvgIconName | undefined = followLevelIcons[level]
  return icon ? `${GlyphMediaVariant.icon}/${icon}` : undefined
}

export const FollowAction = ({ post, action }: Props) => {
  const { defaultLabel, variant, glyphId, color, activeGlyphId, activeColor } =
    action
  const { isFirstRender } = useFirstRenderContext()
  const { level, following, applyLevel } = useFollowLevel(post)
  const levelLabels = useFollowLevelLabels()

  const { isLoggedIn, showLogin } = useLogin()
  const { showText, showIcon } = getTextIconSettings(variant)

  const text = level ? levelLabels[level] : defaultLabel

  const defaultIcon = showIcon && (
    <EntityImage
      entity={{
        image: convertIdToGlyph(glyphId),
        name: defaultLabel || '',
      }}
      size="lg"
      displayColor={color}
    />
  )

  const activator = (
    <Button
      variant="secondaryNeutral"
      className="group select-none"
      pressed={following}
      size="lg"
      fullWidth
      as="span"
      leadingIcon={
        level && showIcon ? (
          <EntityImage
            entity={{
              image: convertIdToGlyph(
                activeGlyphId || levelGlyphId(level) || glyphId,
              ),
              name: text ?? '',
            }}
            size="lg"
            displayColor={activeColor || (color ?? '')}
            iconStyle="solid"
          />
        ) : (
          defaultIcon
        )
      }
      aria-label={text}
    >
      {showText && <span className="truncate">{text}</span>}
    </Button>
  )

  if (!isLoggedIn) {
    return (
      <div className="flex-1">
        <Button
          variant="secondaryNeutral"
          className="group select-none"
          size="lg"
          fullWidth
          leadingIcon={defaultIcon}
          aria-label={defaultLabel}
          onClick={e => {
            e.preventDefault()
            showLogin()
          }}
        >
          {showText && <span className="truncate">{defaultLabel}</span>}
        </Button>
      </div>
    )
  }

  // Avoid dropdown id hydration mismatch.
  if (isFirstRender) {
    return <div className="flex-1">{activator}</div>
  }

  return (
    <div className="flex-1">
      <Dropdown placement="bottom-start" className="w-full">
        <Dropdown.ButtonMinimal className="w-full" aria-label={text}>
          {activator}
        </Dropdown.ButtonMinimal>
        <Dropdown.Items className="w-56">
          <FollowLevelMenuItems level={level} onSelect={applyLevel} />
        </Dropdown.Items>
      </Dropdown>
    </div>
  )
}
